import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface LegalDocument {
  id: string;
  name: string;
  title: string;
  description: string;
  consent_type: string;
  content_html: string;
  content_text: string;
  version: string;
  is_active: boolean;
  is_required: boolean;
  is_revocable: boolean;
  compliance_tags: string[];
  legal_basis: string | null;
  created_at: string;
  updated_at: string;
}

const LEGAL_DOCUMENT_TYPES = ['terms_of_service', 'privacy_policy', 'data_processing_consent'];

function LegalDocumentsPage() {
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<LegalDocument | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/settings/consent/templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Filter only legal documents
      const legalDocs = response.data.filter((doc: LegalDocument) =>
        LEGAL_DOCUMENT_TYPES.includes(doc.name)
      );
      setDocuments(legalDocs);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load documents:', err);
      setError('ไม่สามารถโหลดเอกสารกฎหมายได้');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (doc: LegalDocument) => {
    setSelectedDoc(doc);
    setDialogOpen(true);
    setError(null);
    setSuccess(null);
  };

  const handleView = (doc: LegalDocument) => {
    setSelectedDoc(doc);
    setViewDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedDoc) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      
      // Update via PUT request
      await axios.put(
        `${API_URL}/api/v1/settings/consent/templates/${selectedDoc.id}`,
        {
          name: selectedDoc.name,
          title: selectedDoc.title,
          description: selectedDoc.description,
          consent_type: selectedDoc.consent_type,
          content_html: selectedDoc.content_html,
          content_text: selectedDoc.content_text,
          version: selectedDoc.version,
          is_active: selectedDoc.is_active,
          is_required: selectedDoc.is_required,
          is_revocable: selectedDoc.is_revocable,
          compliance_tags: selectedDoc.compliance_tags,
          legal_basis: selectedDoc.legal_basis,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess('บันทึกเอกสารสำเร็จ');
      setDialogOpen(false);
      await loadDocuments();
    } catch (err: any) {
      console.error('Failed to save document:', err);
      setError('ไม่สามารถบันทึกเอกสารได้: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    if (!selectedDoc) return;
    setSelectedDoc({ ...selectedDoc, [field]: value });
  };

  const getDocumentIcon = (name: string) => {
    switch (name) {
      case 'terms_of_service':
        return '📜';
      case 'privacy_policy':
        return '🔒';
      case 'data_processing_consent':
        return '✅';
      default:
        return '📄';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            📋 จัดการเอกสารกฎหมาย
          </Typography>
          <Typography variant="body2" color="text.secondary">
            จัดการเอกสารทางกฎหมาย: ข้อตกลงการใช้งาน, นโยบายความเป็นส่วนตัว, และความยินยอม PDPA
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>เอกสาร</TableCell>
              <TableCell>ชื่อ</TableCell>
              <TableCell>เวอร์ชัน</TableCell>
              <TableCell>สถานะ</TableCell>
              <TableCell>จำเป็น</TableCell>
              <TableCell>ถอนได้</TableCell>
              <TableCell>อัปเดตล่าสุด</TableCell>
              <TableCell align="right">จัดการ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((doc) => (
              <TableRow key={doc.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: '24px' }}>{getDocumentIcon(doc.name)}</span>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {doc.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {doc.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={`v${doc.version}`} size="small" />
                </TableCell>
                <TableCell>
                  <Chip
                    label={doc.is_active ? 'ใช้งาน' : 'ปิดใช้งาน'}
                    size="small"
                    color={doc.is_active ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={doc.is_required ? 'จำเป็น' : 'ไม่จำเป็น'}
                    size="small"
                    color={doc.is_required ? 'error' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={doc.is_revocable ? 'ถอนได้' : 'ถอนไม่ได้'}
                    size="small"
                    color={doc.is_revocable ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {doc.updated_at ? new Date(doc.updated_at).toLocaleDateString('th-TH', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleView(doc)} title="ดูเนื้อหา">
                    <ViewIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleEdit(doc)} color="primary" title="แก้ไข">
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box>
            <Typography variant="h6">แก้ไขเอกสาร</Typography>
            {selectedDoc?.updated_at && (
              <Typography variant="caption" color="text.secondary">
                อัปเดตล่าสุด: {new Date(selectedDoc.updated_at).toLocaleDateString('th-TH', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
              <Tab label="ข้อมูลทั่วไป" />
              <Tab label="เนื้อหา (HTML)" />
              <Tab label="เนื้อหา (Text)" />
            </Tabs>

            {tabValue === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="ชื่อเอกสาร"
                  value={selectedDoc?.title || ''}
                  onChange={(e) => handleFieldChange('title', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="คำอธิบาย"
                  value={selectedDoc?.description || ''}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                />
                <TextField
                  label="เวอร์ชัน"
                  value={selectedDoc?.version || ''}
                  onChange={(e) => handleFieldChange('version', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="ฐานทางกฎหมาย (Legal Basis)"
                  value={selectedDoc?.legal_basis || ''}
                  onChange={(e) => handleFieldChange('legal_basis', e.target.value)}
                  fullWidth
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectedDoc?.is_active || false}
                        onChange={(e) => handleFieldChange('is_active', e.target.checked)}
                      />
                    }
                    label="ใช้งาน"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectedDoc?.is_required || false}
                        onChange={(e) => handleFieldChange('is_required', e.target.checked)}
                      />
                    }
                    label="จำเป็น"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectedDoc?.is_revocable || false}
                        onChange={(e) => handleFieldChange('is_revocable', e.target.checked)}
                      />
                    }
                    label="ถอนได้"
                  />
                </Box>
              </Box>
            )}

            {tabValue === 1 && (
              <Box sx={{ height: 400 }}>
                <ReactQuill
                  value={selectedDoc?.content_html || ''}
                  onChange={(content) => handleFieldChange('content_html', content)}
                  style={{ height: '350px' }}
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ list: 'ordered' }, { list: 'bullet' }],
                      ['link'],
                      ['clean'],
                    ],
                  }}
                />
              </Box>
            )}

            {tabValue === 2 && (
              <TextField
                label="เนื้อหา (Plain Text)"
                value={selectedDoc?.content_text || ''}
                onChange={(e) => handleFieldChange('content_text', e.target.value)}
                fullWidth
                multiline
                rows={15}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>ยกเลิก</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving}
          >
            {saving ? 'กำลังบันทึก...' : 'บันทึก'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedDoc?.title}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" paragraph>
            {selectedDoc?.description}
          </Typography>
          <Box
            dangerouslySetInnerHTML={{ __html: selectedDoc?.content_html || '' }}
            sx={{ '& h2, & h3': { mt: 2, mb: 1 }, '& ul, & ol': { pl: 3 } }}
          />
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              เวอร์ชัน: {selectedDoc?.version} | อัปเดต: {selectedDoc?.updated_at ? new Date(selectedDoc.updated_at).toLocaleDateString('th-TH') : 'N/A'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>ปิด</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LegalDocumentsPage;
